import { useAuth0 } from '@auth0/auth0-react'
import styled from '@emotion/styled'
import { Scope } from '@pubstack/common/src/scope'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/react/utils'
import { ChangeEvent, FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useUserMetadata } from '~/api/api.hook'
import { Colors } from '~/assets/style/colors'
import { AlwaysVisibleScrollbarStyle } from '~/assets/style/utils'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { BaseInputElement as BaseInput } from '~/components/Input'
import { contextState, currentScopeState, loadingState } from '~/state'

const ScopesPopoverContentSearchBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px;
`
const ScopesPopoverContentList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 350px;
  overflow: auto;
  ${AlwaysVisibleScrollbarStyle}
`
const ScopesPopoverContentListItem = styled.div`
  display: block;
  padding: 8px 12px;
  user-select: none;
  cursor: pointer;
  &:hover {
    background: ${Colors.Topaze};
  }
`
const ScopesPopoverContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
const ScopesPopoverContentNoResultWrapper = styled.div`
  padding: 16px;
  text-align: center;
  max-width: 200px;
  margin: 0 auto;
`

const ScopesPopoverContentNoResult: FC<PropsWithChildren<{ currentFilterValue: string }>> = ({ currentFilterValue }) => {
  return (
    <ScopesPopoverContentNoResultWrapper>
      <h5>No results</h5>
      <p>
        We found no results for "{currentFilterValue}".
        <br />
        Check the spelling or use another word.
      </p>
    </ScopesPopoverContentNoResultWrapper>
  )
}

const ScopesPopoverContent: FC<PropsWithChildren<{ scopes: Scope[] }>> = ({ scopes }) => {
  const user = useAuthenticatedUser()
  const currentScope = useAtomValue(currentScopeState)
  const setLoading = useSetAtom(loadingState)
  const { byId: userMetaDataById } = useUserMetadata(null)
  const { getAccessTokenSilently } = useAuth0()
  const [displayedScopes, setDisplayedScopes] = useState([...scopes])
  const [inputScope, setInputScope] = useState('')
  const resetContext = useResetAtom(contextState)
  const filterScopes = (event: ChangeEvent<HTMLInputElement>) => {
    setInputScope(event.target.value)
    setDisplayedScopes(scopes.filter((item) => item.name.toLowerCase().includes(event.target.value.toLowerCase())))
  }
  const inputElement = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputElement.current?.focus()
  })

  const swap = async (scope: Scope) => {
    await userMetaDataById.put({
      scopeId: scope.id,
      alertEmail: user.alertEmail ?? { [currentScope.id]: false },
    })

    const loading = getAccessTokenSilently({ cacheMode: 'off' })
    setLoading(true)
    await loading
    resetContext()
    window.location.reload()
  }

  const ScopesItems = displayedScopes.map((scope, index) => (
    <ScopesPopoverContentListItem onClick={() => swap(scope)} key={index}>
      {scope.name}
    </ScopesPopoverContentListItem>
  ))

  return (
    <ScopesPopoverContentWrapper>
      <ScopesPopoverContentSearchBar>
        <BaseInput iconLeft={'search'} type={'text'} labelIsPlaceholder label={'Search'} onChange={filterScopes} ref={inputElement} />
      </ScopesPopoverContentSearchBar>
      {displayedScopes.length ? <ScopesPopoverContentList>{ScopesItems}</ScopesPopoverContentList> : <ScopesPopoverContentNoResult currentFilterValue={inputScope} />}
    </ScopesPopoverContentWrapper>
  )
}

export default ScopesPopoverContent
